/**
 * SignalCardBlock - Key metric or categorical signal display
 * 
 * Supports two variants:
 * - Numeric: shows value, unit, trend
 * - Categorical: shows text value with color coding
 */

import React from 'react'
import { SignalCardBlock as SignalCardBlockType, MetricValue } from '../../../../lib/cockpit/schemas'
import { useScenario } from '../../scenario/ScenarioContext'

interface SignalCardBlockProps {
  data: SignalCardBlockType['data']
  blockId: string
  title?: string
  compact?: boolean
  showExplain?: boolean
}

const CONFIDENCE_BADGES = {
  very_low: { label: 'Very Low', color: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400' },
  low: { label: 'Low', color: 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400' },
  medium: { label: 'Medium', color: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400' },
  high: { label: 'High', color: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400' },
  very_high: { label: 'Very High', color: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400' },
} as const

const CATEGORICAL_COLORS = {
  green: 'text-green-600 dark:text-green-400 bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800',
  yellow: 'text-yellow-600 dark:text-yellow-400 bg-yellow-50 dark:bg-yellow-900/20 border-yellow-200 dark:border-yellow-800',
  red: 'text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800',
  blue: 'text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800',
  gray: 'text-gray-600 dark:text-gray-400 bg-gray-50 dark:bg-gray-900/20 border-gray-200 dark:border-gray-700',
} as const

function formatValue(value: MetricValue): string {
  if (typeof value === 'number') {
    return value.toLocaleString()
  }
  if (typeof value === 'string') {
    return value
  }
  if (typeof value === 'object' && value !== null) {
    if ('formatted' in value && value.formatted) {
      return value.formatted
    }
    if ('value' in value) {
      return value.value.toLocaleString()
    }
  }
  return String(value)
}

function SignalCardBlock({ data, blockId, title, compact = false, showExplain = false }: SignalCardBlockProps) {
  const { isTransitioning } = useScenario()
  const confidenceBadge = CONFIDENCE_BADGES[data.confidence as keyof typeof CONFIDENCE_BADGES]

  if (data.variant === 'numeric') {
    const trendIcon = {
      up: (
        <svg className="w-4 h-4 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 10l7-7m0 0l7 7m-7-7v18" />
        </svg>
      ),
      down: (
        <svg className="w-4 h-4 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 14l-7 7m0 0l-7-7m7 7V3" />
        </svg>
      ),
      stable: (
        <svg className="w-4 h-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 12h14" />
        </svg>
      ),
      volatile: (
        <svg className="w-4 h-4 text-orange-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 17h8m0 0V9m0 8l-8-8-4 4-6-6" />
        </svg>
      ),
    }

    if (compact) {
      return (
        <div className="flex items-center gap-2">
          <span className={`
            text-2xl font-bold text-gray-900 dark:text-white
            transition-opacity duration-150
            ${isTransitioning ? 'opacity-50' : 'opacity-100'}
          `}>
            {formatValue(data.value)}
          </span>
          {data.unit && (
            <span className="text-sm text-gray-500 dark:text-gray-400">{data.unit}</span>
          )}
          {data.trend && trendIcon[data.trend as keyof typeof trendIcon]}
        </div>
      )
    }

    return (
      <div className="space-y-3">
        {/* Main Value */}
        <div className="flex items-baseline gap-2">
          <span className={`
            text-4xl font-bold text-gray-900 dark:text-white
            transition-opacity duration-150
            ${isTransitioning ? 'opacity-50' : 'opacity-100'}
          `}>
            {formatValue(data.value)}
          </span>
          {data.unit && (
            <span className="text-lg text-gray-500 dark:text-gray-400">{data.unit}</span>
          )}
        </div>

        {/* Trend and timeframe */}
        <div className="flex items-center gap-3">
          {data.trend && (
            <div className="flex items-center gap-1">
              {trendIcon[data.trend as keyof typeof trendIcon]}
              {data.trend_value !== undefined && (
                <span className={`text-sm font-medium ${
                  data.trend === 'up' ? 'text-green-600 dark:text-green-400' :
                  data.trend === 'down' ? 'text-red-600 dark:text-red-400' :
                  'text-gray-600 dark:text-gray-400'
                }`}>
                  {data.trend_value > 0 ? '+' : ''}{data.trend_value}%
                </span>
              )}
            </div>
          )}
          {data.timeframe && (
            <span className="text-sm text-gray-500 dark:text-gray-400">
              {data.timeframe}
            </span>
          )}
        </div>

        {/* Confidence badge */}
        <div className="flex items-center gap-2">
          <span className={`
            inline-flex items-center px-2 py-0.5 rounded text-xs font-medium
            ${confidenceBadge.color}
          `}>
            {confidenceBadge.label} confidence
          </span>
          
          {data.evidence_anchors.length > 0 && (
            <span className="text-xs text-gray-400 dark:text-gray-500">
              {data.evidence_anchors.length} source{data.evidence_anchors.length !== 1 ? 's' : ''}
            </span>
          )}
        </div>

        {/* Explain section */}
        {showExplain && (
          <div className="mt-3 pt-3 border-t border-gray-200 dark:border-gray-700">
            <div className="text-xs text-gray-500 dark:text-gray-400 space-y-1">
              <p><strong>What it means:</strong> This metric represents a key signal for the analysis.</p>
              <p><strong>How computed:</strong> Aggregated from {data.evidence_anchors.length} source(s).</p>
            </div>
          </div>
        )}
      </div>
    )
  }

  // Categorical variant
  const colorClass = data.color ? CATEGORICAL_COLORS[data.color as keyof typeof CATEGORICAL_COLORS] : CATEGORICAL_COLORS.gray

  if (compact) {
    return (
      <div className={`
        inline-flex items-center px-3 py-1.5 rounded-lg border font-medium
        ${colorClass}
        transition-opacity duration-150
        ${isTransitioning ? 'opacity-50' : 'opacity-100'}
      `}>
        {data.value}
      </div>
    )
  }

  return (
    <div className="space-y-3">
      <div className={`
        inline-flex items-center px-4 py-2 rounded-xl border text-lg font-semibold
        ${colorClass}
        transition-opacity duration-150
        ${isTransitioning ? 'opacity-50' : 'opacity-100'}
      `}>
        {data.value}
      </div>

      {/* Confidence badge */}
      <div className="flex items-center gap-2">
        <span className={`
          inline-flex items-center px-2 py-0.5 rounded text-xs font-medium
          ${confidenceBadge.color}
        `}>
          {confidenceBadge.label} confidence
        </span>
        
        {data.evidence_anchors.length > 0 && (
          <span className="text-xs text-gray-400 dark:text-gray-500">
            {data.evidence_anchors.length} source{data.evidence_anchors.length !== 1 ? 's' : ''}
          </span>
        )}
      </div>

      {/* Explain section */}
      {showExplain && (
        <div className="mt-3 pt-3 border-t border-gray-200 dark:border-gray-700">
          <div className="text-xs text-gray-500 dark:text-gray-400 space-y-1">
            <p><strong>What it means:</strong> This categorical assessment represents the current state.</p>
            <p><strong>How determined:</strong> Based on analysis of {data.evidence_anchors.length} source(s).</p>
          </div>
        </div>
      )}
    </div>
  )
}

export default SignalCardBlock



